import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChatTeardropText, CheckCircle } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";

const field = "w-full rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] px-3 py-2.5 text-sm focus:border-[#00FF9D] focus:outline-none transition-colors";

const Score = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="How likely are you to recommend CalibRunway">
    {Array.from({ length: 11 }, (_, n) => (
      <button key={n} type="button" role="radio" aria-checked={value === n} data-testid={`feedback-score-${n}`} onClick={() => onChange(n)}
        className={`h-10 w-10 rounded-lg border text-sm font-mono transition-colors ${value === n ? "border-[#00FF9D] bg-[#00FF9D] !text-[#050505] font-semibold" : "border-[#2A2A2A] hover:border-white"}`}>{n}</button>
    ))}
  </div>
);

export default function Feedback() {
  const api = useApi();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["feedback"], queryFn: () => api.get("/feedback") });
  const [f, setF] = useState({ score: null, outcome: "", role: "", display_name: "", quote_ok: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/feedback", f); toast.success("Thank you! Your feedback is in."); await qc.invalidateQueries({ queryKey: ["feedback"] }); }
    catch (err) { toast.error(errText(err, "Couldn't send feedback")); }
    setBusy(false);
  };
  if (data?.submitted) return (
    <div data-testid="feedback-done" className="max-w-xl rounded-2xl border border-[#2A2A2A] bg-[#121212] p-8">
      <CheckCircle size={32} className="text-[#00FF9D]" />
      <h1 className="font-display text-2xl mt-4">Thank you for your feedback</h1>
      <p className="mt-2 text-sm text-[#A3A3A3]">It goes straight to Sonia, the founder, and shapes what we build next.</p>
    </div>
  );
  const ok = f.score !== null && f.outcome.trim().length >= 3;
  return (
    <form data-testid="feedback-form" onSubmit={submit} className="max-w-2xl">
      <p className="font-mono text-xs text-[#A3A3A3] flex items-center gap-2"><ChatTeardropText className="text-[#00FF9D]" /> TWO MINUTES</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">How is CalibRunway working for you?</h1>
      <div className="mt-8 space-y-7">
        <div>
          <p className="text-sm mb-3">How likely are you to recommend CalibRunway to another founder? <span className="text-[#A3A3A3]">(0 = not at all, 10 = definitely)</span></p>
          <Score value={f.score} onChange={(score) => setF({ ...f, score })} />
        </div>
        <label className="block">
          <span className="text-sm">What changed for you since you started? <span className="text-[#A3A3A3]">For example, how your Zero Cash Date moved or a decision it helped you make.</span></span>
          <textarea data-testid="feedback-outcome" required maxLength={1000} rows={5} value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} className={`${field} mt-2`} />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="text-sm">Your role and stage <span className="text-[#A3A3A3]">(optional)</span></span>
            <input data-testid="feedback-role" maxLength={120} placeholder="e.g. Co-founder, pre-seed SaaS" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={`${field} mt-2`} /></label>
          <label className="block"><span className="text-sm">Name to show with a quote <span className="text-[#A3A3A3]">(optional)</span></span>
            <input data-testid="feedback-name" maxLength={80} placeholder="e.g. Maya R." value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} className={`${field} mt-2`} /></label>
        </div>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox data-testid="feedback-quote-ok" checked={f.quote_ok} onCheckedChange={(v) => setF({ ...f, quote_ok: !!v })} className="mt-0.5" />
          <span>You may quote my answer on the CalibRunway website, with the name and role above. <span className="text-[#A3A3A3]">We'll check with you before anything goes live.</span></span>
        </label>
      </div>
      <button data-testid="feedback-submit-btn" disabled={!ok || busy}
        className="mt-8 rounded-full bg-[#00FF9D] px-7 py-3 text-sm font-semibold text-[#050505] hover:bg-white transition-colors disabled:opacity-40">{busy ? "Sending…" : "Send feedback"}</button>
    </form>
  );
}
